// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
//  Author    : Albert Charlier, Dirk Rothe, Tobias Schwarzkopf
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 156393 $
//  Purpose   : facets
// =============================================================================
// --- Third-party imports
import React from 'react'
import {Label, Menu, Accordion, Form, Segment, Button as SuiButton} from 'semantic-ui-react'
import clsx from 'clsx'

// --- Framework imports
import {get, filterItems, dict, mapItems, size} from 'ldash'
import log from 'uix/logging'
import {translateLangCode} from 'uix/defs'
import {Actions, Sel} from 'uix/registries'
import {useState, useSelector, useActions, usePersistent, useCallback} from 'uix/hooks'
import {XPopup, XMenu, Link, DebugFeature} from 'uix/components'

// --- Local imports
import styles from './facets.scss'
import {Button, Form as NativeForm} from './nativeComps'

// --- Globals
const EMPTY_TOKEN = 'zzz'

const useFacetNavigation = ({paramName = 'facets'} = {}) => {
  let go = useActions('go')
  let toggleFacetValue = (checked, value, incquery, decquery) => {
    let queryFacets = checked ? incquery : decquery
    let paramValue = null
    if (queryFacets) {
      paramValue = queryFacets.replace('$(facetval)', value)
    }
    go({updateQuery: {[paramName]: paramValue, offset: null}})
  }
  return toggleFacetValue
}

export const FacetAlphaIndex = ({type = 'title', facets, className}) => {
  let toggleFacetValue = useFacetNavigation()
  let fname = `alpha.${type}1`
  let data = facets?.[fname]

  if (!data && facets) return null //`no data for ${fname}`

  let alphabet = 'abcdefghijklmnopqrstuvwxyz'.split('')
  let byChar = dict(data.items.map(({value, ...rest}) => [value, rest]))

  let {incquery, decquery} = data
  let isSelected = c => byChar[c]?.selected
  let isAvailable = c => byChar[c]?.count > 0
  return (
    <div className={clsx(className, styles.facetAlpha)}>
      <Button className={styles.navigator} content="<" />
      <div className={styles.alpha}>
        {alphabet.map(c => (
          <Button
            key={c}
            disabled={!isAvailable(c)}
            active={isSelected(c)}
            className={clsx(
              styles.item,
              isAvailable(c) && styles.available,
              isSelected(c) && styles.active
            )}
            onClick={() =>
              isAvailable(c) ? toggleFacetValue(!isSelected(c), c, incquery, decquery) : null
            }
          >
            {c}
          </Button>
        ))}
      </div>
      <Button className={styles.navigator} content=">" />
    </div>
  )
}

export const Facets = ({
  hideEmpty = true,
  facets,
  mode,
  className,
  paramName,
  bare = false, // so outer segment in sui mode
}) => {
  let i18n = useSelector(Sel.i18n)
  let language = i18n.language
  let request = useSelector(Sel.request)
  let itypes = useSelector(Sel.itypes)
  let domains = useSelector(Sel.domains)
  let toggleFacetValue = useFacetNavigation({paramName})

  let [active, setActive] = useState({}) // is facet box open (accordion)
  let [showAll, setShowAll] = useState({})

  let toggleBox = (facetName, wasActive) => {
    setActive({...active, [facetName]: !wasActive})
    if (wasActive && showAll[facetName]) {
      setShowAll({...showAll, [facetName]: false})
    }
  }

  let _toggleFacetValue = (facetName, isChecked, valueName, incquery, decquery) => {
    toggleBox(facetName, false) // ensure box is opened if individual values are switched
    toggleFacetValue(isChecked, valueName, incquery, decquery)
  }

  let translateLabel = (name, value, caption) => {
    if (caption) {
      return caption
    } else if (value == 'zzz') {
      return i18n['empty']
    } else {
      switch (name) {
        case 'collections':
          return value
        case 'pagemode':
          return i18n[`pagemodeFacet_${value}`]
        case 'language':
          return translateLangCode(value, language)
        case 'contentSource':
          return i18n[`ISIL-${value}`]
        case 'odid':
          return get(domains.data, `${domains.odid2name[value]}.description`, value)
        case 'domainGroup':
          return get(domains.data, `${value}.description`, value)
        case 'licence':
          return value
        case 'released':
          return i18n[`ot_state_${value}`]
        case 'state.metadata':
          return i18n[`metadata_state_${value}`]
        case 'state.qs':
          return i18n[`ot_qs_state_${value}`]
        case 'ftmode':
          return value == 'empty' ? i18n['ftmodeWithoutFulltext'] : i18n['ftmodeWithFulltext']
        case 'subtype':
          return i18n[`types_metadata_${itypes.byId[1][value]}`]
        case 'name.subtype':
          return i18n[`types_name_${itypes.byId[13][value]}`]
        case 'subject.subtype':
          return i18n[`types_subject_${itypes.byId[19][value]}`]
        case 'name.subjects':
          return i18n[`gnd_subj_${value}`]
        case 'name.countries':
          return i18n[`gnd_reg_${value}`]
        case 'subject.gnd.sc1':
          return i18n[`gnd_subj_${value}`]
        case 'subject.gnd.sc2':
          return i18n[`gnd_subj_${value}`]
        case 'type':
          return i18n[`types_metadata_${value}`]

        default:
          if ('d100 dr100 d50 dr50 d10 dr10'.indexOf(name) > -1) {
            let period = parseInt(name.replace(/\D/g, ''))
            if (value == EMPTY_TOKEN) {
              return i18n['bracketUnknown']
            }
            let year = parseInt(value)
            // fetch from dconfig
            let dateShift = 1
            return `${parseInt(year)}-${year + period - 1 + dateShift}`
          }
          return value
      }
    }
  }

  let isSelected = item => item.selected
  let filledFacets = filterItems(
    facets,
    (name, data) => data.items && (data.items.length > 1 || data.items.some(isSelected))
  )

  filledFacets = filledFacets.filter(([name]) => !name.startsWith('alpha.'))

  if (filledFacets.length == 0) {
    return null
  }

  let get_is_empty_hidden = name => {
    return hideEmpty
  }
  const SHOW_MANY = 100
  const SHOW_LESS = 6
  const SHOW_ALL_FACET_NAMES = ['d10', 'd100']

  let get_limit = name => {
    if (SHOW_ALL_FACET_NAMES.includes(name)) {
      return SHOW_MANY * 10
    }
    if (showAll[name]) {
      return SHOW_MANY
    }
    return SHOW_LESS
  }

  let boxes = filledFacets.map(([name, data], i) => {
    // do not show d10 facet, when d100 exists and is not selected
    if (name === 'd10' && facets.d100 && !get(facets, 'd100.items', []).some(isSelected)) {
      return null
    }

    let item = ({value, count, caption, selected, decquery}) => {
      let label_count = {}
      if (mode === 'sui') {
        label_count = (
          <>
            <span style={{float: 'right', paddingRight: '3px', fontSize: '0.9em'}}>{count}</span>
            <label style={{display: 'flex', flex: 1}}>{translateLabel(name, value, caption)}</label>
          </>
        )
      } else {
        label_count = (
          <>
            <span>{translateLabel(name, value, caption)} </span>
            <span
              className="count"
              style={{marginLeft: 'auto', float: 'right', paddingRight: '3px', fontSize: '0.9em'}}
            >
              {count}
            </span>
          </>
        )
      }
      if (mode === 'sui') {
        return (
          <Form.Checkbox
            key={value}
            label={label_count}
            name={`${name}_${value}`}
            checked={selected}
            style={{width: '100%'}}
            onChange={(e, r) => _toggleFacetValue(name, r.checked, value, data.incquery, decquery)}
          />
        )
      } else {
        return (
          <NativeForm.Checkbox
            className={styles.checkbox}
            key={value}
            label={label_count}
            checked={selected}
            onChange={e =>
              _toggleFacetValue(name, e.target.checked, value, data.incquery, decquery)
            }
          />
        )
      }
    }

    let filter_items = e => e.value // has a value
    if (get_is_empty_hidden(name)) {
      filter_items = e => e.value && e.value != 'zzz'
    }
    const items = data.items
      .filter(filter_items)
      .slice(0, get_limit(name))
      .map(e => item(e))

    let label = i18n[`index_${name}s`]

    let group = {}
    if (mode === 'sui') {
      group = (
        <Form>
          <Form.Group grouped>
            <div className={styles.itemBox}>{items}</div>
          </Form.Group>
        </Form>
      )
    } else {
      group = (
        <NativeForm.Group key={i} title={label || name}>
          {items}
        </NativeForm.Group>
      )
    }
    let someSelected = data.items.some(isSelected)
    let isActive = active[name] !== undefined ? active[name] : someSelected
    if (mode === 'sui') {
      let clickShowAll = (e, name) => {
        // do not propagate click event
        e.stopPropagation()
        setShowAll({...showAll, [name]: !showAll[name]})
      }
      let boxHeader = <>{label || name} </>
      return (
        <Menu.Item key={name}>
          <Accordion.Title
            active={isActive}
            content={boxHeader}
            index={i}
            onClick={() => toggleBox(name, isActive)}
          />
          <Accordion.Content active={isActive}>
            {group}
            {isActive &&
              !SHOW_ALL_FACET_NAMES.includes(name) &&
              !someSelected &&
              !showAll[name] && (
                <SuiButton
                  secondary
                  compact
                  size="mini"
                  icon={{name: !showAll[name] ? 'plus' : 'minus', color: 'black'}}
                  style={{position: 'absolute', left: '40%', bottom: '0px', lineHeight: '0.8em'}}
                  content="mehr"
                  onClick={e => clickShowAll(e, name)}
                  title={
                    !showAll[name]
                      ? `Show ${Math.min(size(data.items), SHOW_MANY)} of ${size(data.items)}`
                      : ''
                  }
                />
              )}
          </Accordion.Content>
        </Menu.Item>
      )
    } else {
      return group
    }
  })

  if (mode === 'sui') {
    let content = (
      <>
        <Accordion as={Menu} vertical attached={bare ? true : undefined}>
          {boxes}
        </Accordion>
        <DebugFeature>
          <div style={{fontSize: '0.7em', marginTop: '-10px'}}>total: {facets.numhits}</div>
        </DebugFeature>
      </>
    )
    if (bare) {
      return content
    }
    return (
      <Segment raised style={{maxHeight: '80vh', overflow: 'auto'}}>
        {content}
      </Segment>
    )
  } else {
    {
      return (
        <div className={className}>
          <NativeForm>{boxes}</NativeForm>
        </div>
      )
    }
  }
}

export const FacetPopupMenuItem = ({facets, mode, paramName, ...props}) => {
  let [showFacets, setShowFacets] = usePersistent('showFacets', false)
  return (
    <XMenu.PopupItem position="bottom right" icon="tasks" title="Show Facets" {...props}>
      <Facets facets={facets} mode={mode} paramName={paramName} />
    </XMenu.PopupItem>
  )
}
